import {
  MessageSquare,
  Users,
  Settings,
  Cable,
  Moon,
  Tags,
  AlarmClock,
  BookText,
  Bot,
  ChartNoAxesCombined,
  Headset,
  TrendingUpDown,
  Funnel,
  Archive,
  Sun,
  DoorOpen,
  Contact,
  PhoneCall,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import useDarkMode from "../hooks/useDarkMode";
import { useWaitingResponse } from "../hooks/useWaitingResponse";
import ChatJuriLogo from "../assets/logo.png";

const Sidebar = ({ activeView, setActiveView }) => {
  const { user, logout } = useAuth();

  const [theme, toggleTheme] = useDarkMode();
  const [userExpanded, setUserExpanded] = useState(false);
  // Um cliente esperando demais precisa aparecer mesmo com o gestor em outra
  // tela; o contador no menu é o que faz o problema chegar até ele.
  const { waiting } = useWaitingResponse({ pollMs: 60000 });
  const overdueCount = waiting?.summary?.overdue || 0;

  const navigationItems = [
    { id: "conversations", icon: MessageSquare, title: "Conversas" },
    { id: "analysis", icon: ChartNoAxesCombined, title: "Análise de Atendimentos" },
    { id: "active", icon: Headset, title: "Em Atendimento" },
    { id: "waiting", icon: AlarmClock, title: "Aguardando Resposta", badge: overdueCount },
    // Desempenho nominal da equipe, como em Usuários: só ADMIN.
    { id: "calls", icon: PhoneCall, title: "Ligações", adminOnly: true },
    // A gestão de usuários mostra desempenho nominal da equipe e é restrita no
    // backend; esconder o item evita levar quem não é ADMIN a uma tela com 403.
    { id: "users", icon: Users, title: "Usuários", adminOnly: true },
    { id: "scripts", icon: BookText, title: "Roteiros de Atendimento" },
    { id: "automations", icon: Bot, title: "Automações", adminOnly: true },
    { id: "contacts", icon: Contact, title: "Contatos" },
    { id: "tags", icon: Tags, title: "Tags" },
    { id: "funnel", icon: Funnel, title: "Funil" },
    { id: "attachment", icon: Archive, title: "Anexos" },
    {
      id: "quickMessages",
      icon: TrendingUpDown,
      title: "Mensagens Rápidas",
    },
    {
      id: "WhatsAppConnectionInterface",
      icon: Cable,
      title: "WhatsAppConnectionInterface",
    },
  ];

  return (
    <div className="flex w-20 flex-col items-center space-y-4 bg-gray-900 py-4">
      {/* Logo */}
      <div className="flex h-12 w-12 items-center justify-center rounded-lg font-bold text-white">
        <img src={ChatJuriLogo} alt="ChatJuri" />
      </div>

      {/* Navigation Items */}
      <nav className="scrollbar-hide flex flex-1 flex-col space-y-2 overflow-y-auto border-t border-gray-800 py-4">
        {navigationItems.map((item) => {
          if (item.adminOnly && user?.role !== "ADMIN") return null;

          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`relative rounded-lg p-3 transition-colors ${
                activeView === item.id
                  ? "bg-green-500 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-white"
              }`}
              title={
                item.badge > 0
                  ? `${item.title} — ${item.badge} fora do prazo`
                  : item.title
              }
            >
              <Icon className="h-6 w-6" />

              {item.badge > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <button
        onClick={() => setUserExpanded(!userExpanded)}
        className={`flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-white ${
          activeView === "user"
            ? "bg-green-600 "
            : "bg-green-500 hover:bg-green-600"
        }`}
      >
        {user?.avatar ? (
          <img
            src={process.env.REACT_APP_URL + user.avatar}
            alt="Avatar"
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          user?.name?.charAt(0)?.toUpperCase() || "U"
        )}
      </button>

      <AnimatePresence>
        {userExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col items-center space-y-2 overflow-hidden"
          >
            {" "}
            <button
              onClick={toggleTheme}
              className="rounded-lg p-3 transition-colors hover:bg-gray-800 hover:text-white"
            >
              {theme === "dark" ? (
                <Sun className="h-6 w-6 text-yellow-600" />
              ) : (
                <Moon className="h-6 w-6 text-blue-600" />
              )}
            </button>
            {/* Settings */}
            <button
              onClick={() => setActiveView("settings")}
              className={`rounded-lg p-3 transition-colors ${
                activeView === "settings"
                  ? "bg-green-500 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-white"
              }`}
              title="Configurações"
            >
              <Settings className="h-6 w-6" />
            </button>
            {/* Logout */}
            <button
              onClick={() => logout()}
              className="rounded-lg p-3 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
              title="Logout"
            >
              <DoorOpen className="h-6 w-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Theme Toggle */}
    </div>
  );
};

export default Sidebar;
